import { PrimaryRelationProfileViewDto } from './../../../profiles/shared/dto/primary-relation-profile-view.dto';
import { PagedResultGeneric } from 'src/app/shared';
import { SelectItem } from 'primeng/api';
import { InvoiceUpsertDto } from '../../dto/invoice-upsert.dto';
import { InvoiceService } from './../../../revenue/shared/invoice/invoice.service';
import { Component, OnInit, OnChanges, Input, ViewEncapsulation, SimpleChanges } from '@angular/core';
import { ProfileMin } from './../../../profiles/shared/profile-min.model';
import { ProfileInvoiceDefaultsDto } from './../../../profiles/shared/dto/profile-invoice-defaults.dto';
import { ProfileRelationSearch } from './../../../profiles/shared/profile-relation-search.model';
import { ProfileService } from './../../../profiles/shared/profile.service';
import { UserService } from 'src/app/users/shared/user.service';
import { VerticalService } from '../../../vertical/shared/vertical.service';
import { VerticalTypeEnum } from '../../../vertical/shared/vertical-type.enum';
import { TranslationService } from 'src/app/shared';
import { RevenueObservationService } from 'src/app/revenue/shared/revenue-observation.service';

@Component({
  selector: 'wl-invoice-details',
  templateUrl: './invoice-details.component.html',
  styleUrls: ['./invoice-details.component.less'],
  encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class InvoiceDetailsComponent implements OnInit, OnChanges {
  @Input() invoice: InvoiceUpsertDto;

  public selectedCommProfileId: number;
  public relatedProfiles: Array<SelectItem> = [];
  public activeUsers: Array<SelectItem> = [];
  public terms: Array<string>;
  public filteredTerms: Array<string>;
  public hideinvoiceType: boolean;
  public invoiceTypes: Array<SelectItem> = [];
  private _cleanInvoiceHeader: string;

  constructor(
    private _profileService: ProfileService,
    private _invoiceService: InvoiceService,
    private _userService: UserService,
    private _verticalTypeService: VerticalService,
    private _translateService: TranslationService,
    private _revenueObservationService: RevenueObservationService
    ) {
    this.terms = new Array<string>();
  }

  ngOnInit() {
    if (this.invoice.InvoiceHeader.DateSent == null) {
      this.invoice.InvoiceHeader.DateSent = new Date();
    }

    this._userService.getActiveUsers().then((users) => {
      this.activeUsers = users
        .map<SelectItem>((user) => {
          return { label: user.UserName, value: user.UserNum };
        })
        .sort((x, y) => {
          if (x.label.toLowerCase() > y.label.toLowerCase()) {
            return 1;
          }
          if (x.label.toLowerCase() < y.label.toLowerCase()) {
            return -1;
          }
          return 0;
        });
    });

    this._invoiceService.getTerms().then((data: Array<string>) => {
      this.terms = data;
    });

    this._verticalTypeService.GetCurrentUserVerticalType().then((verticalType) => {
      this.hideinvoiceType = verticalType != VerticalTypeEnum.AssociatedBuildersAndContractors;
    });

    setTimeout(() => {
      this.invoiceTypes = [
        {
          label: this._translateService.getInstant('invoices.invoice-builder.details-tab.invoice-types.new-label'),
          value: 'New'
        },
        {
          label: this._translateService.getInstant('invoices.invoice-builder.details-tab.invoice-types.renew-label'),
          value: 'Renew'
        },
        { label: this._translateService.getInstant('invoices.invoice-builder.details-tab.invoice-types.adjust-label'),
          value: 'Adjust'
        }
      ];
    });

    this._revenueObservationService.observeRequestInvoiceUpsert().subscribe(() => {
      this.setDirty();
    });

    this._revenueObservationService.observeInvoiceLoaded().subscribe(() => {
      this.setCleanObject();
    });

    this.setCleanObject();
  }

  ngOnChanges(changes:SimpleChanges) {
    if (this.invoice.InvoiceHeader.ProfileId) {
      const search = new ProfileRelationSearch();
      search.PageNumber = 1;
      search.PageSize = 99999;
      search.ProfileId = this.invoice.InvoiceHeader.ProfileId;
      this._profileService.getProfileViewPrimaryRelations(search, true).then((data: PagedResultGeneric<PrimaryRelationProfileViewDto>) => {
        this.relatedProfiles.push({
          label: this._translateService.getInstantCapitalizeFirstCharacter('common.none'),
          value: 0
        })
        const relatedProfileOptions = data.Result.map((x) => {
          return { label: x.ReportName, value: x.ProfileId };
        });
        this.relatedProfiles = this.relatedProfiles.concat(relatedProfileOptions);
        this._profileService.getInvoiceDefaults(this.invoice.InvoiceHeader.ProfileId).then((data: ProfileInvoiceDefaultsDto) => {
          this.setInvoiceDefaults(data);
        });
      });
    }
  }

  public selectedCommProfileChanged() {
    if (this.selectedCommProfileId !== null) {
      const profile = this.relatedProfiles.find((x) => x.value === this.selectedCommProfileId);
      if (profile) {
        this.invoice.InvoiceHeader.CommunicateWithProfileID = profile.value;
        this.invoice.InvoiceHeader.Attention = profile.label;
      }
    }
  }

  get dirty() {
    return JSON.stringify(this.invoice.InvoiceHeader) !== this._cleanInvoiceHeader;
  }

  private setDirty() {
    this.invoice.InvoiceHeader.Dirty = this.dirty;
  }

  private setCleanObject() {
    this._cleanInvoiceHeader = JSON.stringify(this.invoice.InvoiceHeader);
  }

  private setInvoiceDefaults(defaults: ProfileInvoiceDefaultsDto, changingProfile: boolean = false) {
    if (this.invoice.InvoiceHeader.InvoiceNum && !changingProfile) {
      this.selectedCommProfileId = this.invoice.InvoiceHeader.CommunicateWithProfileID;
    } else {
      if (defaults.DefaultAttentionToProfileId) {
        this.selectedCommProfileId = defaults.DefaultAttentionToProfileId;
        this.selectedCommProfileChanged();
      }

      if (defaults.DefaultBillToAddress) {
        this.invoice.InvoiceHeader.BillToAddress1 = defaults.DefaultBillToAddress.Address1;
        this.invoice.InvoiceHeader.BillToAddress2 = defaults.DefaultBillToAddress.Address2;
        this.invoice.InvoiceHeader.BillToCity = defaults.DefaultBillToAddress.City;
        this.invoice.InvoiceHeader.BillToState = defaults.DefaultBillToAddress.State;
        this.invoice.InvoiceHeader.BillToZip = defaults.DefaultBillToAddress.Zip;
        this.invoice.InvoiceHeader.BillToZipExt = defaults.DefaultBillToAddress.ZipExt;
      }

      if (defaults.DefaultShipToAddress) {
        this.invoice.InvoiceHeader.ShipToAddress1 = defaults.DefaultShipToAddress.Address1;
        this.invoice.InvoiceHeader.ShipToAddress2 = defaults.DefaultShipToAddress.Address2;
        this.invoice.InvoiceHeader.ShipToCity = defaults.DefaultShipToAddress.City;
        this.invoice.InvoiceHeader.ShipToState = defaults.DefaultShipToAddress.State;
        this.invoice.InvoiceHeader.ShipToZip = defaults.DefaultShipToAddress.Zip;
        this.invoice.InvoiceHeader.ShipToZipExt = defaults.DefaultShipToAddress.ZipExt;
      }

      this._profileService.getProfileMin(this.invoice.InvoiceHeader.ProfileId).then((data: ProfileMin) => {
        this.invoice.InvoiceHeader.BillToName = data.ReportName;
        this.invoice.InvoiceHeader.ShipToName = data.ReportName;
      });
    }
  }

  public filterTerms(event) {
    this.filteredTerms = [];
    for (let i = 0; i < this.terms.length; i++) {
      let term = this.terms[i];
      if (term.toLowerCase().indexOf(event.query.toLowerCase()) == 0) {
        this.filteredTerms.push(term);
      }
    }
  }

  async invoiceProfileChanged() {
    const search = new ProfileRelationSearch();
    search.PageNumber = 1;
    search.PageSize = 99999;
    search.ProfileId = this.invoice.InvoiceHeader.ProfileId;

    const rP = (await this._profileService.getProfileViewPrimaryRelations(search, true))?.Result ?? [];
    if (rP) {
      this.relatedProfiles = rP.map((x) => {
        return { label: x.ReportName, value: x.ProfileId };
      });
    }

    const defaults = await this._profileService.getInvoiceDefaults(this.invoice.InvoiceHeader.ProfileId);

    if (defaults) {
      this.setInvoiceDefaults(defaults, true);
    }
  }
}
